import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/lib/api";
import { Header } from "@/components/header";
import { Button, Card, ErrorView, Field, Loading } from "@/components/kit";
import { useToast } from "@/components/toast";

const PRESETS = ["Construction neuve", "Rénovation", "Extension", "Maison individuelle", "Gros œuvre", "Toiture", "Isolation", "Aménagement intérieur"];
const EMPTY = {
  company_name: "", siret: "", contact_name: "", phone: "", email: "", address: "", city: "", department: "", departments_covered: "",
  specialties: [], other_specialties: "", insurance_decennale: false, insurance_rc_pro: false, insurance_expiry: "", insurance_notes: "",
  commission_rate: "", notes: "", active: true,
};

export function Toggle({ label, value, onChange, testID }) {
  return (
    <div className="flex min-h-11 items-center gap-3">
      <span id={`${testID}-label`} className="flex-1 text-sm text-white">{label}</span>
      <button type="button" role="switch" aria-checked={value} aria-labelledby={`${testID}-label`} data-testid={testID} onClick={() => onChange(!value)}
        className={`focus-ring relative h-7 w-12 shrink-0 rounded-full transition-colors ${value ? "bg-[#27AE60]" : "bg-[#555555]"}`}>
        <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function fromMaster(m) {
  return {
    company_name: m.company_name, siret: m.siret, contact_name: m.contact_name ?? "", phone: m.phone ?? "", email: m.email ?? "",
    address: m.address ?? "", city: m.city, department: m.department, departments_covered: m.departments_covered.join(", "),
    specialties: m.specialties.filter((s) => PRESETS.includes(s)), other_specialties: m.specialties.filter((s) => !PRESETS.includes(s)).join(", "),
    insurance_decennale: m.insurance_decennale, insurance_rc_pro: m.insurance_rc_pro, insurance_expiry: m.insurance_expiry ?? "",
    insurance_notes: m.insurance_notes ?? "", commission_rate: m.commission_rate === null ? "" : String(m.commission_rate).replace(".", ","), notes: m.notes ?? "", active: m.status === "active",
  };
}

export default function MasterForm() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const existing = useQuery({ queryKey: ["admin-master", id], queryFn: () => api(`/admin/masters/${id}`), enabled: !!id });
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const bind = (k) => ({ value: f[k], onChangeText: (v) => set(k, v) });

  useEffect(() => {
    if (existing.data) setF(fromMaster(existing.data));
  }, [existing.data]);

  const rate = f.commission_rate.trim() ? Number(f.commission_rate.replace(",", ".")) : null;
  const valid = f.company_name.trim() && f.siret.replace(/\D/g, "").length === 14 && f.city.trim() && f.department.trim() && (rate === null || (!isNaN(rate) && rate >= 0 && rate <= 100));

  const save = async (e) => {
    e?.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const body = {
        company_name: f.company_name, siret: f.siret, contact_name: f.contact_name || null, phone: f.phone || null, email: f.email.trim() || null,
        address: f.address || null, city: f.city, department: f.department,
        departments_covered: f.departments_covered.split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        specialties: [...f.specialties, ...f.other_specialties.split(",").map((s) => s.trim()).filter(Boolean)],
        insurance_decennale: f.insurance_decennale, insurance_rc_pro: f.insurance_rc_pro, insurance_expiry: f.insurance_expiry.trim() || null,
        insurance_notes: f.insurance_notes || null, commission_rate: rate, notes: f.notes || null, status: f.active ? "active" : "inactive",
      };
      const m = await api(id ? `/admin/masters/${id}` : "/admin/masters", { method: id ? "PUT" : "POST", body });
      ["admin-masters", "admin-geo", "admin-dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries({ queryKey: ["admin-master", m.id] });
      toast(id ? "Fiche mise à jour." : "Maître d'œuvre ajouté.");
      if (id) navigate(-1);
      else navigate(`/admin/master/${m.id}`, { replace: true });
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="master-form-screen" className="flex flex-col">
      <Header title={id ? "Modifier la fiche" : "Nouveau maître d'œuvre"} />
      {id && existing.isLoading ? <Loading /> : id && existing.isError ? <ErrorView message="Fiche introuvable." onRetry={() => existing.refetch()} /> : (
        <form onSubmit={save} noValidate className="mx-auto grid w-full max-w-5xl items-start gap-4 p-4 pb-8 md:p-8 lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <Card className="flex flex-col gap-3">
              <h2 className="t-h3">Entreprise</h2>
              <Field testID="master-company-input" label="Nom de l'entreprise *" {...bind("company_name")} />
              <Field testID="master-siret-input" label="SIRET (14 chiffres) *" inputMode="numeric" placeholder="123 456 789 00012" {...bind("siret")}
                error={f.siret && f.siret.replace(/\D/g, "").length !== 14 ? "14 chiffres attendus" : undefined} />
            </Card>
            <Card className="flex flex-col gap-3">
              <h2 className="t-h3">Coordonnées</h2>
              <Field testID="master-contact-input" label="Interlocuteur" {...bind("contact_name")} />
              <div className="grid gap-3 md:grid-cols-2">
                <Field testID="master-phone-input" label="Téléphone" type="tel" {...bind("phone")} />
                <Field testID="master-email-input" label="E-mail" type="email" {...bind("email")} />
              </div>
              <Field testID="master-address-input" label="Adresse" {...bind("address")} />
              <div className="grid gap-3 md:grid-cols-2">
                <Field testID="master-city-input" label="Ville *" placeholder="Lille" {...bind("city")} />
                <Field testID="master-department-input" label="Département du siège *" placeholder="59 ou Nord" {...bind("department")} />
              </div>
            </Card>
            <Card className="flex flex-col gap-3">
              <h2 className="t-h3">Départements couverts</h2>
              <Field testID="master-departments-input" label="Numéros ou noms, séparés par des virgules" placeholder="59, 62, 80" {...bind("departments_covered")} />
              <p className="t-caption">Le département du siège est ajouté automatiquement.</p>
            </Card>
          </div>
          <div className="flex flex-col gap-4">
            <Card className="flex flex-col gap-3">
              <h2 className="t-h3">Spécialités</h2>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p, i) => {
                  const on = f.specialties.includes(p);
                  return (
                    <button key={p} type="button" data-testid={`master-specialty-${i}`} aria-pressed={on}
                      onClick={() => set("specialties", on ? f.specialties.filter((s) => s !== p) : [...f.specialties, p])}
                      className={`focus-ring h-9 rounded-full border px-3 text-sm font-medium transition-colors ${on ? "border-[#D4AF37] bg-[#292415] text-[#D4AF37]" : "border-[#333333] bg-[#2A2A2A] text-[#CCCCCC] hover:border-[#555555]"}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
              <Field testID="master-other-specialties-input" label="Autres (séparées par des virgules)" {...bind("other_specialties")} />
            </Card>
            <Card className="flex flex-col gap-2">
              <h2 className="t-h3">Assurances vérifiées</h2>
              <Toggle testID="master-insurance-decennale" label="Garantie décennale vérifiée" value={f.insurance_decennale} onChange={(v) => set("insurance_decennale", v)} />
              <Toggle testID="master-insurance-rcpro" label="RC professionnelle vérifiée" value={f.insurance_rc_pro} onChange={(v) => set("insurance_rc_pro", v)} />
              <Field testID="master-insurance-expiry-input" label="Échéance des attestations (AAAA-MM-JJ)" placeholder="2027-06-30" {...bind("insurance_expiry")} />
              <Field testID="master-insurance-notes-input" label="Assureur / n° de police" {...bind("insurance_notes")} />
            </Card>
            <Card className="flex flex-col gap-3">
              <h2 className="t-h3">Commission négociée (indicative)</h2>
              <Field testID="master-commission-input" label="Taux habituel (%) — facultatif" inputMode="decimal" placeholder="5" {...bind("commission_rate")} />
              <p className="t-caption">Simple repère : la commission réelle est fixée librement sur chaque dossier, en fonction du chantier.</p>
            </Card>
            <Card className="flex flex-col gap-3">
              <h2 className="t-h3">Notes internes</h2>
              <Field testID="master-notes-input" label="Notes" multiline {...bind("notes")} />
              <Toggle testID="master-active-switch" label="Partenaire actif" value={f.active} onChange={(v) => set("active", v)} />
            </Card>
            <Button testID="master-save-button" type="submit" title={id ? "Enregistrer" : "Ajouter le maître d'œuvre"} size="lg" loading={busy} disabled={!valid} />
          </div>
        </form>
      )}
    </div>
  );
}
